/* eslint-disable react/prop-types */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    AlertCircle, AlertTriangle, CheckCircle, ChevronRight, Copy, Download,
    RefreshCw, RotateCcw, Shield, ShieldOff, ShieldCheck, X,
} from "lucide-react";
import classNames from "classnames";
import BuscadorInput from "../../../Store/BuscadorInput.jsx";
import { buildDownloadUrl } from "./downloadUrl.js";

// ── Constantes (estados reales del backend) ─────────────────────────────────

const STATUS_BADGE = {
    activo:   "badge-success",
    revocado: "badge-error",
    agotado:  "badge-warning",
};

const TABS = [
    { id: "entitlements", label: "Licencias" },
    { id: "failures",     label: "Fallos de concesión" },
    { id: "reconcile",    label: "Reconciliación" },
];

const useDebounce = (value, delay) => {
    const [dv, setDv] = useState(value);
    useEffect(() => {
        const h = setTimeout(() => setDv(value), delay);
        return () => clearTimeout(h);
    }, [value, delay]);
    return dv;
};

// ── Componentes auxiliares ───────────────────────────────────────────────────

function StatusIcon({ status }) {
    if (status === "activo")   return <ShieldCheck className="w-4 h-4 text-success" />;
    if (status === "revocado") return <ShieldOff   className="w-4 h-4 text-error"   />;
    if (status === "agotado")  return <Shield      className="w-4 h-4 text-warning" />;
    return <Shield className="w-4 h-4 text-base-content/40" />;
}

function DetailField({ label, children }) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-xs text-base-content/50 uppercase tracking-wide">{label}</span>
            <span className="text-sm">{children ?? <span className="text-base-content/30">—</span>}</span>
        </div>
    );
}

// ── Modal de confirmación con motivo ─────────────────────────────────────────

function ReasonModal({
    title, description, confirmLabel, confirmVariant = "error",
    customerVisible = false, onConfirm, onCancel,
}) {
    const [reason, setReason] = useState("");
    const [busy, setBusy]     = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    const handleConfirm = async () => {
        if (!reason.trim()) return;
        setBusy(true);
        try {
            await onConfirm(reason.trim());
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-base-100 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-base">{title}</h3>
                    <button onClick={onCancel} className="btn btn-xs btn-ghost btn-circle shrink-0" aria-label="Cancelar">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {description && <p className="text-sm text-base-content/70">{description}</p>}
                {customerVisible && (
                    <div
                        role="note"
                        className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning-content dark:text-warning"
                    >
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                        <span>Este motivo será visible para el cliente en su panel de descargas.</span>
                    </div>
                )}
                <div className="form-control">
                    <label className="label pb-1">
                        <span className="label-text text-sm font-medium">Motivo <span className="text-error">*</span></span>
                    </label>
                    <textarea
                        ref={inputRef}
                        className="textarea textarea-bordered textarea-sm w-full resize-none"
                        rows={3}
                        placeholder={
                            customerVisible
                                ? "Ej. «Reembolso solicitado por el cliente.» — se mostrará tal cual."
                                : "Describe el motivo de esta acción…"
                        }
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        maxLength={500}
                    />
                    <label className="label pt-0.5">
                        <span className="label-text-alt text-base-content/40">{reason.length}/500</span>
                    </label>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <button onClick={onCancel} className="btn btn-sm btn-ghost" disabled={busy}>
                        Cancelar
                    </button>
                    <button
                        onClick={handleConfirm}
                        className={`btn btn-sm btn-${confirmVariant}`}
                        disabled={!reason.trim() || busy}
                    >
                        {busy
                            ? <span className="loading loading-spinner loading-xs" />
                            : confirmLabel
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Panel de token emitido (se muestra una sola vez) ──────────────────────────

function TokenResult({ token, expiresAt, onClose }) {
    const [copied, setCopied] = useState(false);
    // El enlace lo sirve la API, que en producción vive en otro dominio que el
    // frontend; por eso se construye contra la base de la API, no contra el origen.
    const apiBase = import.meta.env.VITE_APP_API_BASE_URL || axios.defaults.baseURL || "";
    const tokenUrl = buildDownloadUrl(apiBase, token);

    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(tokenUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // fallback: select
        }
    };

    return (
        <div className="rounded-lg border border-warning/40 bg-warning/5 p-4 space-y-3">
            <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-warning">
                    Token generado — visible una sola vez. Cópialo ahora.
                </p>
            </div>
            <div className="bg-base-200 rounded p-2 font-mono text-xs break-all select-all">
                {tokenUrl}
            </div>
            <div className="flex items-center gap-2 text-xs text-base-content/60">
                <span>Caduca: {new Date(expiresAt).toLocaleString("es-ES")}</span>
            </div>
            <div className="flex gap-2">
                <button onClick={doCopy} className="btn btn-sm btn-ghost gap-1">
                    <Copy className="w-3 h-3" />
                    {copied ? "¡Copiado!" : "Copiar enlace"}
                </button>
                <button onClick={onClose} className="btn btn-sm btn-ghost text-base-content/50">
                    Cerrar
                </button>
            </div>
        </div>
    );
}

// ── Panel de detalle de entitlement ──────────────────────────────────────────

function EntitlementDetail({ entId, onClose, onRefreshList }) {
    const [detail, setDetail]         = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [msg, setMsg]               = useState(null);
    const [modal, setModal]           = useState(null);   // { action: 'revoke'|'restore' }
    const [tokenResult, setTokenResult] = useState(null); // { token, expiresAt }
    const [busyToken, setBusyToken]   = useState(false);

    const fetchDetail = useCallback(() => {
        setLoading(true);
        setError(null);
        axios
            .get(`/digital-entitlements-admin/${entId}/`, { withCredentials: true })
            .then((r) => setDetail(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Error al cargar detalle."))
            .finally(() => setLoading(false));
    }, [entId]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    const doAction = useCallback(async (action, reason, successMsg) => {
        setMsg(null);
        try {
            await axios.post(
                `/digital-entitlements-admin/${entId}/${action}/`,
                { reason },
                { withCredentials: true },
            );
            setMsg({ type: "success", text: successMsg });
            fetchDetail();
            onRefreshList?.();
        } catch (e) {
            setMsg({ type: "error", text: e.response?.data?.detail || `Error al ejecutar ${action}.` });
        }
    }, [entId, fetchDetail, onRefreshList]);

    const doIssueToken = useCallback(async () => {
        setBusyToken(true);
        setMsg(null);
        setTokenResult(null);
        try {
            const r = await axios.post(
                `/digital-entitlements-admin/${entId}/issue-token/`,
                {},
                { withCredentials: true },
            );
            // El token completo se muestra una sola vez; nunca se guarda en estado persistente ni consola.
            setTokenResult({ token: r.data.token, expiresAt: r.data.expires_at });
        } catch (e) {
            setMsg({ type: "error", text: e.response?.data?.detail || "Error al generar token." });
        } finally {
            setBusyToken(false);
        }
    }, [entId]);

    if (loading) return (
        <div className="flex items-center justify-center h-40">
            <span className="loading loading-spinner loading-lg text-primary" />
        </div>
    );

    if (error) return (
        <div className="text-center py-8">
            <AlertCircle className="w-8 h-8 text-error mx-auto mb-2" />
            <p className="text-sm text-base-content/70">{error}</p>
            <button onClick={fetchDetail} className="btn btn-xs btn-outline mt-3">Reintentar</button>
        </div>
    );

    if (!detail) return null;

    return (
        <>
            {/* Modal de motivo */}
            {modal?.action === "revoke" && (
                <ReasonModal
                    title="Revocar licencia"
                    description="El usuario perderá el acceso a la descarga. Esta acción queda auditada."
                    confirmLabel="Revocar"
                    confirmVariant="error"
                    customerVisible
                    onCancel={() => setModal(null)}
                    onConfirm={async (reason) => {
                        // Cerrar solo al terminar: mientras la petición viaja, el
                        // modal sigue montado mostrando su spinner de carga.
                        await doAction("revoke", reason, "Entitlement revocado.");
                        setModal(null);
                    }}
                />
            )}
            {modal?.action === "restore" && (
                <ReasonModal
                    title="Restaurar licencia"
                    description="El usuario recuperará el acceso a la descarga. El motivo anterior de revocación deja de mostrarse al cliente."
                    confirmLabel="Restaurar"
                    confirmVariant="success"
                    onCancel={() => setModal(null)}
                    onConfirm={async (reason) => {
                        // Cerrar solo al terminar (ver nota en el modal de revocar).
                        await doAction("restore", reason, "Entitlement restaurado.");
                        setModal(null);
                    }}
                />
            )}

            <div className="flex flex-col gap-4">
                {/* Cabecera */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        <StatusIcon status={detail.status} />
                        <div>
                            <p className="font-semibold text-sm leading-tight">{detail.design_name || `Ent. #${detail.id}`}</p>
                            <p className="text-xs text-base-content/50">{detail.beneficiary_email}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="btn btn-xs btn-ghost btn-circle" aria-label="Cerrar detalle">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Mensaje de operación */}
                {msg && (
                    <div role="alert" className={`alert alert-${msg.type === "success" ? "success" : "error"} py-2 text-sm`}>
                        {msg.type === "success"
                            ? <CheckCircle className="w-4 h-4 shrink-0" />
                            : <AlertCircle className="w-4 h-4 shrink-0" />
                        }
                        <span>{msg.text}</span>
                    </div>
                )}

                {/* Token resultado (una sola vez) */}
                {tokenResult && (
                    <TokenResult
                        token={tokenResult.token}
                        expiresAt={tokenResult.expiresAt}
                        onClose={() => setTokenResult(null)}
                    />
                )}

                {/* Campos */}
                <div className="grid grid-cols-2 gap-3">
                    <DetailField label="Estado">
                        <span className={`badge badge-sm ${STATUS_BADGE[detail.status] || "badge-ghost"}`}>
                            {detail.status_display || detail.status}
                        </span>
                    </DetailField>
                    <DetailField label="Causa">{detail.grant_cause}</DetailField>
                    <DetailField label="Concedido">
                        {detail.granted_at ? new Date(detail.granted_at).toLocaleString("es-ES") : null}
                    </DetailField>
                    <DetailField label="Pedido">#{detail.order_id}</DetailField>
                    <DetailField label="Descargas">
                        {detail.download_count}
                        {detail.download_limit != null && ` / ${detail.download_limit}`}
                    </DetailField>
                    <DetailField label="Paquete">{detail.package_state}</DetailField>
                    {detail.revoked_at && (
                        <DetailField label="Revocado">
                            {new Date(detail.revoked_at).toLocaleString("es-ES")}
                        </DetailField>
                    )}
                    {detail.revocation_reason && (
                        <DetailField label="Motivo revocación">{detail.revocation_reason}</DetailField>
                    )}
                </div>

                {/* Acciones */}
                <div className="flex flex-wrap gap-2 pt-1 border-t border-base-200">
                    {detail.status === "activo" && (
                        <button
                            className="btn btn-xs btn-error gap-1"
                            onClick={() => setModal({ action: "revoke" })}
                            aria-label="Revocar entitlement"
                        >
                            <ShieldOff className="w-3 h-3" />
                            Revocar
                        </button>
                    )}
                    {detail.status === "revocado" && (
                        <button
                            className="btn btn-xs btn-success gap-1"
                            onClick={() => setModal({ action: "restore" })}
                            aria-label="Restaurar entitlement"
                        >
                            <ShieldCheck className="w-3 h-3" />
                            Restaurar
                        </button>
                    )}
                    <button
                        className="btn btn-xs btn-ghost gap-1"
                        onClick={doIssueToken}
                        disabled={busyToken || detail.status !== "activo"}
                        aria-label="Generar token de email"
                    >
                        {busyToken
                            ? <span className="loading loading-spinner loading-xs" />
                            : <Download className="w-3 h-3" />
                        }
                        Generar token
                    </button>
                    <button
                        className="btn btn-xs btn-ghost gap-1"
                        onClick={fetchDetail}
                        disabled={loading}
                        aria-label="Recargar detalle"
                    >
                        <RefreshCw className="w-3 h-3" />
                        Recargar
                    </button>
                </div>

                {/* Historial de eventos */}
                {detail.events?.length > 0 && (
                    <div>
                        <p className="text-xs uppercase tracking-wide text-base-content/50 mb-2">Historial</p>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                            {detail.events.map((ev, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                    <span className="text-base-content/40 whitespace-nowrap">
                                        {new Date(ev.created_at).toLocaleString("es-ES")}
                                    </span>
                                    <ChevronRight className="w-3 h-3 text-base-content/30 shrink-0" />
                                    <span>{ev.action}</span>
                                    {ev.reason && <span className="text-base-content/50 truncate">— {ev.reason}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Últimas descargas */}
                {detail.download_events?.length > 0 && (
                    <div>
                        <p className="text-xs uppercase tracking-wide text-base-content/50 mb-2">Descargas recientes</p>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                            {detail.download_events.map((ev, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                    <span className="text-base-content/40 whitespace-nowrap">
                                        {new Date(ev.created_at).toLocaleString("es-ES")}
                                    </span>
                                    <ChevronRight className="w-3 h-3 text-base-content/30 shrink-0" />
                                    <span className={ev.success ? "text-success" : "text-error"}>
                                        {ev.success ? "Iniciada" : ev.failure_reason || "Error"}
                                    </span>
                                    <span className="text-base-content/40">{ev.channel}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

// ── Pestaña: lista de entitlements ─────────────────────────────────────────

function TabEntitlements() {
    const [items, setItems]           = useState([]);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selected, setSelected]     = useState(null);

    const debouncedSearch = useDebounce(searchTerm, 350);

    const fetchItems = useCallback((q = "") => {
        setLoading(true);
        setError(null);
        const params = q ? { search: q } : {};
        axios
            .get("/digital-entitlements-admin/", { params, withCredentials: true })
            .then((r) => setItems(r.data.results || r.data || []))
            .catch((e) => setError(e.response?.data?.detail || "Error al cargar entitlements."))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchItems(debouncedSearch); }, [fetchItems, debouncedSearch]);

    const showEmpty = !loading && !error && items.length === 0;

    return (
        <div className="flex gap-3 h-full min-h-0">
            {/* Lista */}
            <div className={classNames(
                "flex flex-col gap-3 min-w-0",
                selected ? "w-1/2 hidden lg:flex" : "w-full",
            )}>
                <div className="flex items-center gap-2">
                    <div className="flex-1">
                        <BuscadorInput
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            placeholder="Buscar por email, diseño o nº pedido…"
                        />
                    </div>
                    <button
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={() => fetchItems(debouncedSearch)}
                        disabled={loading}
                        aria-label="Actualizar"
                    >
                        {loading
                            ? <span className="loading loading-spinner loading-xs" />
                            : <RefreshCw className="w-4 h-4" />
                        }
                    </button>
                </div>

                {error && (
                    <div role="alert" className="alert alert-error py-2 text-sm">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {showEmpty && (
                    <div className="text-center py-10 text-base-content/50">
                        <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
                        <p>{debouncedSearch ? `Sin resultados para "${debouncedSearch}"` : "No hay entitlements."}</p>
                    </div>
                )}

                {!error && items.length > 0 && (
                    <div className="overflow-x-auto flex-1">
                        <table className="table table-sm" aria-label="Entitlements">
                            <thead>
                                <tr className="text-xs uppercase">
                                    <th>Diseño / Email</th>
                                    <th>Estado</th>
                                    <th className="text-center">DL</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((ent) => (
                                    <tr
                                        key={ent.id}
                                        className={classNames("hover cursor-pointer", selected?.id === ent.id && "bg-base-200")}
                                        onClick={() => setSelected(ent)}
                                    >
                                        <td>
                                            <div className="text-sm font-medium leading-tight">{ent.design_name || `#${ent.id}`}</div>
                                            <div className="text-xs text-base-content/50 truncate max-w-[12rem]">
                                                {ent.beneficiary_email || (ent.order_id && `Pedido #${ent.order_id}`)}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`badge badge-sm ${STATUS_BADGE[ent.status] || "badge-ghost"}`}>
                                                {ent.status_display || ent.status}
                                            </span>
                                        </td>
                                        <td className="text-center text-sm">{ent.download_count}</td>
                                        <td>
                                            <ChevronRight className="w-4 h-4 text-base-content/30" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Panel de detalle */}
            {selected && (
                <div className="w-full lg:w-1/2 glassCard p-4 overflow-y-auto">
                    <EntitlementDetail
                        entId={selected.id}
                        onClose={() => setSelected(null)}
                        onRefreshList={() => fetchItems(debouncedSearch)}
                    />
                </div>
            )}
        </div>
    );
}

// ── Pestaña: fallos de concesión ────────────────────────────────────────────

function TabFailures() {
    const [failures, setFailures] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [retrying, setRetrying] = useState(null);
    const [msg, setMsg]           = useState(null);

    const fetchFailures = useCallback(() => {
        setLoading(true);
        setError(null);
        axios
            .get("/digital-entitlements-admin/grant-failures/", { withCredentials: true })
            .then((r) => setFailures(r.data.results || r.data || []))
            .catch((e) => setError(e.response?.data?.detail || "Error al cargar fallos."))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchFailures(); }, [fetchFailures]);

    const doRetry = useCallback(async (f) => {
        setRetrying(f.id);
        setMsg(null);
        try {
            await axios.post(
                `/digital-entitlements-admin/grant-failures/${f.id}/retry/`,
                {},
                { withCredentials: true },
            );
            setMsg({ type: "success", text: `Reintento exitoso para item #${f.order_item_id}.` });
            fetchFailures();
        } catch (e) {
            setMsg({ type: "error", text: e.response?.data?.detail || "Error al reintentar." });
        } finally {
            setRetrying(null);
        }
    }, [fetchFailures]);

    const showEmpty = !loading && !error && failures.length === 0;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <p className="text-sm text-base-content/70">
                    Fallos al conceder entitlements (pagos confirmados sin acceso).
                </p>
                <button
                    className="btn btn-sm btn-ghost btn-circle"
                    onClick={fetchFailures}
                    disabled={loading}
                    aria-label="Actualizar"
                >
                    {loading
                        ? <span className="loading loading-spinner loading-xs" />
                        : <RefreshCw className="w-4 h-4" />
                    }
                </button>
            </div>

            {msg && (
                <div role="alert" className={`alert alert-${msg.type === "success" ? "success" : "error"} py-2 text-sm`}>
                    {msg.type === "success"
                        ? <CheckCircle className="w-4 h-4 shrink-0" />
                        : <AlertCircle className="w-4 h-4 shrink-0" />
                    }
                    <span>{msg.text}</span>
                </div>
            )}

            {error && (
                <div role="alert" className="alert alert-error py-2 text-sm">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {showEmpty && (
                <div className="text-center py-10 text-base-content/50">
                    <CheckCircle className="w-10 h-10 mx-auto mb-3 text-success opacity-60" />
                    <p>No hay fallos pendientes.</p>
                </div>
            )}

            {!error && failures.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="table table-sm" aria-label="Fallos de concesión">
                        <thead>
                            <tr className="text-xs uppercase">
                                <th>Item</th>
                                <th>Error</th>
                                <th>Fecha</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {failures.map((f) => (
                                <tr key={f.id} className="hover">
                                    <td className="text-sm font-mono">#{f.order_item_id}</td>
                                    <td className="text-sm max-w-[14rem] truncate text-error/80">
                                        {f.error_message}
                                    </td>
                                    <td className="text-sm whitespace-nowrap text-base-content/60">
                                        {new Date(f.created_at).toLocaleString("es-ES")}
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-xs btn-ghost gap-1"
                                            onClick={() => doRetry(f)}
                                            disabled={retrying === f.id}
                                            aria-label={`Reintentar fallo #${f.id}`}
                                        >
                                            {retrying === f.id
                                                ? <span className="loading loading-spinner loading-xs" />
                                                : <RotateCcw className="w-3 h-3" />
                                            }
                                            Reintentar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── Pestaña: reconciliar ────────────────────────────────────────────────────

function TabReconcile() {
    const [running, setRunning] = useState(false);       // 'simulate' | 'repair' | false
    const [result, setResult]   = useState(null);
    const [error, setError]     = useState(null);
    const [confirmRepair, setConfirmRepair] = useState(false);

    const run = useCallback(async (mode) => {
        setRunning(mode);
        setResult(null);
        setError(null);
        try {
            const r = await axios.post(
                "/digital-entitlements-admin/reconcile/",
                { dry_run: mode === "simulate" },
                { withCredentials: true },
            );
            setResult({ ...r.data, _mode: mode });
        } catch (e) {
            setError(e.response?.data?.detail || "Error al reconciliar.");
        } finally {
            setRunning(false);
        }
    }, []);

    return (
        <div className="flex flex-col gap-4 max-w-2xl">
            {/* Explicación en lenguaje claro. */}
            <div className="rounded-lg border border-base-300 bg-base-200/40 p-4 text-sm">
                <p className="font-medium mb-1">¿Qué hace esta pestaña?</p>
                <p className="text-base-content/80 leading-relaxed">
                    Busca pagos digitales confirmados que no recibieron su licencia de descarga
                    (por ejemplo, si el proceso murió justo después de cobrar) y permite crearla ahora.
                    La operación es idempotente: ejecutarla varias veces no duplica nada.
                </p>
            </div>

            {/* Dos acciones claramente separadas: simular vs reparar. */}
            <div className="grid gap-3 sm:grid-cols-2">
                {/* Simular */}
                <div className="rounded-lg border border-base-300 p-4 flex flex-col gap-3">
                    <div className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-info shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium">Simular</p>
                            <p className="text-xs text-base-content/60 mt-0.5 leading-snug">
                                Solo lectura: comprueba cuántos pagos están sin licencia. No modifica nada.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline gap-2 self-start"
                        onClick={() => run("simulate")}
                        disabled={!!running}
                        aria-label="Simular reconciliación (sin cambios)"
                    >
                        {running === "simulate"
                            ? <><span className="loading loading-spinner loading-xs" />Simulando…</>
                            : <><Shield className="w-4 h-4" />Simular</>
                        }
                    </button>
                </div>

                {/* Reparar ahora */}
                <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 flex flex-col gap-3">
                    <div className="flex items-start gap-2">
                        <RotateCcw className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium">Reparar ahora</p>
                            <p className="text-xs text-base-content/60 mt-0.5 leading-snug">
                                Escribe en la base de datos: crea las licencias que faltan y las deja disponibles en el perfil del cliente.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-primary gap-2 self-start"
                        onClick={() => setConfirmRepair(true)}
                        disabled={!!running}
                        aria-label="Reparar ahora (con confirmación)"
                    >
                        {running === "repair"
                            ? <><span className="loading loading-spinner loading-xs" />Reparando…</>
                            : <><RotateCcw className="w-4 h-4" />Reparar ahora…</>
                        }
                    </button>
                </div>
            </div>

            {error && (
                <div role="alert" className="alert alert-error py-2 text-sm">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {result && (
                <div className="rounded-lg border border-base-300 p-4 text-sm space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="font-medium flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-success" />
                            {result._mode === "simulate" ? "Resultado (simulación)" : "Resultado"}
                        </p>
                        <span className={classNames(
                            "badge badge-sm",
                            result._mode === "simulate"
                                ? "badge-info"
                                : result.errors > 0
                                    ? "badge-error"
                                    : result.created > 0
                                        ? "badge-primary"
                                        : "badge-ghost",
                        )}>
                            {result._mode === "simulate"
                                ? "Sin cambios (simulación)"
                                : result.errors > 0
                                    ? "Finalizado con errores"
                                    : result.created > 0
                                        ? "Cambios aplicados"
                                        : "Sin cambios necesarios"
                            }
                        </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                            { label: "Encontradas", value: result.found, cls: "" },
                            { label: "Creadas", value: result.created, cls: "text-success" },
                            { label: "Ya existían", value: result.existing, cls: "text-info" },
                            { label: "Errores", value: result.errors, cls: "text-error" },
                        ].map(({ label, value, cls }) => (
                            <div key={label} className="bg-base-200 rounded p-2 text-center">
                                <div className={`text-xl font-bold ${cls || "text-base-content"}`}>{value ?? 0}</div>
                                <div className="text-xs text-base-content/60">{label}</div>
                            </div>
                        ))}
                    </div>
                    {result.no_payment > 0 && (
                        <p className="text-xs text-warning">
                            {result.no_payment} líneas sin pago confirmado asociado.
                        </p>
                    )}
                </div>
            )}

            {/* Confirmación explícita antes de escribir. */}
            {confirmRepair && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                    <div className="bg-base-100 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-warning shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-base mb-1">Ejecutar reparación real</h3>
                                <p className="text-sm text-base-content/70">
                                    Se van a crear las licencias digitales que falten para pagos ya confirmados.
                                    La operación es idempotente, pero afecta a datos reales.
                                </p>
                                <p className="text-xs text-base-content/50 mt-2">
                                    ¿Prefieres simular primero? Cancela y usa <span className="font-medium">Simular</span>.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button
                                type="button"
                                className="btn btn-sm btn-ghost"
                                onClick={() => setConfirmRepair(false)}
                                disabled={running === "repair"}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary gap-2"
                                onClick={async () => {
                                    setConfirmRepair(false);
                                    await run("repair");
                                }}
                                disabled={running === "repair"}
                            >
                                <RotateCcw className="w-4 h-4" />
                                Sí, reparar ahora
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Componente raíz ─────────────────────────────────────────────────────────

const EntitlementsAdmin = () => {
    const [tab, setTab] = useState("entitlements");

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            {/* Cabecera */}
            <div className="glassCard p-4 z-40">
                <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-6 h-6 text-primary" />
                    <div>
                        <h2 className="text-xl font-semibold">Licencias digitales</h2>
                        <p className="text-sm text-base-content/60">
                            Licencias, fallos de concesión y reconciliación
                        </p>
                    </div>
                </div>
                {/* Tabs con scroll horizontal en móvil para que no se corten. */}
                <div className="-mx-1 overflow-x-auto scrollbar_mod">
                    <div
                        role="tablist"
                        className="tabs tabs-boxed tabs-sm w-max min-w-full flex-nowrap px-1"
                    >
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                role="tab"
                                className={classNames("tab whitespace-nowrap", tab === t.id && "tab-active")}
                                onClick={() => setTab(t.id)}
                                aria-selected={tab === t.id}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Contenido de la pestaña activa */}
            <div className="flex-1 overflow-y-auto scrollbar_mod glassCard p-4 min-h-0">
                {tab === "entitlements" && <TabEntitlements />}
                {tab === "failures"     && <TabFailures />}
                {tab === "reconcile"    && <TabReconcile />}
            </div>
        </div>
    );
};

export default EntitlementsAdmin;
